
import { Component, Prop, Vue } from 'vue-property-decorator';
import Icon from '@/layouts/Icon.vue';
import { SubsidyAvailableDataListType } from '@/plugins/coupon';
import CouponApplyDateComponent, { CouponApplyDate, CouponApplyDateMaster } from '@/components/subs01/CouponApplyDateComponent.vue';
import { DateTime } from 'luxon';
import EnvRepository from '@/repository/EnvRepository';
import { TargetPerson } from './TargetPersonFormComponent.vue';

@Component({
  components: {
    CouponApplyDateComponent,
    Icon,
  },
  filters: {
    comma(value?: number) {
      if (!value) return 0;
      return value.toLocaleString();
    },
    formatYYYYMMDD(str?: string) {
      if (!str) return '';

      const dateTime = DateTime.fromISO(str);
      if (!dateTime.isValid) return '不正な値';

      return dateTime.toFormat('yyyy/MM/dd');
    },
  },
})
export default class CouponApplyRootComponent extends Vue {
  @Prop({ type: Object, required: true })
  availableDataRoot!: SubsidyAvailableDataListType;

  @Prop({ type: Array, required: true })
  targetPersonList!: TargetPerson[];

  @Prop({ type: Boolean, required: true })
  disabled!: boolean;

  @Prop({ type: Array, required: true })
  couponApplyDateList!: CouponApplyDate[];

  @Prop({ type: Array, required: true })
  useDateListMaster!: string[];

  selectedCouponApplicableNo: number | '' = '';

  errorMessage = '';

  couponApplyDateMasters: CouponApplyDateMaster[] = [];

  get appliedCount(): number {
    return this.couponApplyDateList.map((data) => data.useDates.length).reduce((previousValue, currentValue) => previousValue + currentValue, 0);
  }

  get sum(): number {
    return this.couponApplyDateList
      .flatMap((couponApplyDate) => couponApplyDate.useDates.map((data) => data.price))
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0);
  }

  get generateUsedFormDivLabel(): string {
    // validate.
    if (!this.availableDataRoot.availableData) return '';

    switch (this.availableDataRoot.availableData.usedFormDiv) {
      case '01':
        return '自動補助クーポン利用';

      case '02':
        return '補助クーポン利用';

      default:
        return '';
    }
  }

  get getDispSubsidyNm(): string {
    // validate.
    if (!this.availableDataRoot.availableData) return '';

    return this.availableDataRoot.availableData.dispSubsidyNm ?? '';
  }

  get getRemainingCount(): number {
    return this.availableDataRoot.remainingCount ?? 0;
  }

  created(): void {
    this.couponApplyDateList.forEach((data) => {
      const oneTargetPerson = this.targetPersonList.find((targetPerson) => targetPerson.couponApplicableNo === Number(data.couponApplicableNo));

      if (oneTargetPerson) {
        const useDateOptions = this.generateCouponApplyDateMaster(oneTargetPerson);

        if (!useDateOptions) return;

        this.couponApplyDateMasters.push({ useDateOptions });
      }
    });
  }

  clickAdd(): void {
    // validate.
    this.validateAdd();
    if (this.errorMessage) return;

    if (!this.availableDataRoot.availableData || !this.availableDataRoot.availableData.priceList) return;

    const oneTargetPerson = this.targetPersonList.find((targetPerson) => targetPerson.couponApplicableNo === Number(this.selectedCouponApplicableNo));

    if (!oneTargetPerson) return;

    const useDateOptions = this.generateCouponApplyDateMaster(oneTargetPerson);

    if (!useDateOptions) return;

    this.couponApplyDateMasters.push({ useDateOptions });

    this.couponApplyDateList.push({
      ...oneTargetPerson,
      ...{
        subsidyId: this.availableDataRoot.availableData.subsidyId ?? '',
        subsidyDetailId: this.availableDataRoot.availableData.subsidyDetailId ?? '',
        usedFromDiv: this.availableDataRoot.availableData.usedFormDiv ?? '',
        priceType: oneTargetPerson.relationship === '0' ? '本人' : '家族',
        costUnderChargeableFlg: this.availableDataRoot.availableData.costUnderChargeableFlg ?? '',
        multipleUnusableFlg: this.availableDataRoot.availableData.multipleUnusableFlg ?? '',
        minChargeAmt: this.availableDataRoot.availableData.minChargeAmt,
        useDates: useDateOptions,
      },
    });
  }

  clickDelete(deleteObj: CouponApplyDate): void {
    // eslint-disable-next-line no-restricted-syntax
    for (const [index, obj] of this.couponApplyDateList.entries()) {
      if (obj.couponApplicableNo === deleteObj.couponApplicableNo) {
        this.couponApplyDateList.splice(index, 1);
        this.couponApplyDateMasters.splice(index, 1);
        break;
      }
    }
  }

  disabledTargetPerson(targetPerson: TargetPerson): boolean {
    // validate.
    if (
      !this.availableDataRoot.availableData ||
      !this.availableDataRoot.availableData.applicableDegreeCnt ||
      (!Number(targetPerson.degreeCount) && targetPerson.degreeCount !== '0')
    ) {
      return true;
    }

    const validDegree = Number(targetPerson.degreeCount) <= this.availableDataRoot.availableData.applicableDegreeCnt;
    const validAgeType: boolean =
      targetPerson.ageType === '大人' ||
      (targetPerson.ageType === '子供' &&
        (this.availableDataRoot.availableData.minTgtAge === undefined ||
          (this.availableDataRoot.availableData.minTgtAge !== undefined &&
            targetPerson.age !== '' &&
            targetPerson.age > this.availableDataRoot.availableData.minTgtAge)));

    return !(validDegree && validAgeType);
  }

  private generateCouponApplyDateMaster(targetPerson: TargetPerson) {
    if (!this.availableDataRoot.availableData || !this.availableDataRoot.availableData.priceList) return null;
    const { priceList } = this.availableDataRoot.availableData;

    return this.useDateListMaster.map((useDate) => {
      const date = DateTime.fromISO(useDate);
      let priceData = priceList.find((obj) => {
        const from = DateTime.fromISO(obj.limApplicableDateFrom ?? '');
        const to = DateTime.fromISO(obj.limApplicableDateTo ?? '');

        return (
          date.isValid &&
          from.isValid &&
          to.isValid &&
          obj.priceType === (targetPerson.relationship === '0' ? '本人' : '家族') &&
          obj.ageType === targetPerson.ageType &&
          from <= date &&
          date <= to
        );
      });

      // not founded, set 一律 of ageType data.
      if (!priceData) {
        priceData = priceList.find((obj) => {
          const from = DateTime.fromISO(obj.limApplicableDateFrom ?? '');
          const to = DateTime.fromISO(obj.limApplicableDateTo ?? '');

          return (
            date.isValid &&
            from.isValid &&
            to.isValid &&
            obj.priceType === (targetPerson.relationship === '0' ? '本人' : '家族') &&
            obj.ageType === '一律' &&
            from <= date &&
            date <= to
          );
        });
      }

      // not founded, set 一律 of priceType data.
      if (!priceData) {
        priceData = priceList.find((obj) => {
          const from = DateTime.fromISO(obj.limApplicableDateFrom ?? '');
          const to = DateTime.fromISO(obj.limApplicableDateTo ?? '');

          return (
            date.isValid &&
            from.isValid &&
            to.isValid &&
            obj.priceType === '一律' &&
            obj.ageType === targetPerson.ageType &&
            from <= date &&
            date <= to
          );
        });
      }

      // not founded, set 一律 data both priceType and ageType.
      if (!priceData) {
        priceData = priceList.find((obj) => {
          const from = DateTime.fromISO(obj.limApplicableDateFrom ?? '');
          const to = DateTime.fromISO(obj.limApplicableDateTo ?? '');

          return date.isValid && from.isValid && to.isValid && (obj.priceType === '一律' || obj.ageType === '一律') && from <= date && date <= to;
        });
      }

      // not founded, empty data.
      if (!priceData) {
        return {
          useScheduleDateTime: '',
          price: 0,
          subsidyPriceId: '',
        };
      }

      return {
        useScheduleDateTime: useDate,
        price: priceData.price ?? 0,
        subsidyPriceId: priceData.subsidyPriceId ?? '',
      };
    });
  }

  private validateAdd(): void {
    this.errorMessage = '';

    // no select.
    if (!this.selectedCouponApplicableNo) {
      this.errorMessage = EnvRepository.getEnv().VUE_APP_SUBS01_ERR_NO_SELECT_TARGET_PERSON;
      return;
    }

    // duplicated.
    const duplicated = this.couponApplyDateList.some((obj) => obj.couponApplicableNo === Number(this.selectedCouponApplicableNo));
    if (duplicated) {
      this.errorMessage = EnvRepository.getEnv().VUE_APP_SUBS01_ERR_DUPLICATED;
    }
  }
}
